import { useState } from "react";
import { useTheme } from "@emotion/react";
import { alpha } from "@mui/material/styles";
import {
    Autocomplete,
    Checkbox,
    Chip,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { AutoAwesome } from "@mui/icons-material";

// Puts already-selected options first (alphabetically), then the rest (alphabetically),
// so the browsable list surfaces what's picked without hunting through the full list.
const sortSelectedFirst = (options, selectedValues) => {
    const selectedSet = new Set(selectedValues);
    const compareLabels = (a, b) => a.label.localeCompare(b.label);

    const selectedOptions = options
        .filter((option) => selectedSet.has(option.label))
        .sort(compareLabels);
    const restOptions = options
        .filter((option) => !selectedSet.has(option.label))
        .sort(compareLabels);

    return [...selectedOptions, ...restOptions];
};

const AutocompleteWithTags = ({
    label,
    placeholder,
    list = [],
    selectedValues = [],
    onChange,
    onInputValueChange,
    showDropDown = true,
    limitTags = null,
    maxWords,
    minHeight = "60px",
    aiValues = [],
}) => {
    const theme = useTheme();
    const [isFocused, setIsFocused] = useState(false);
    const [inputValue, setInputValue] = useState("");
    // Snapshot of the selected-first order, taken when the dropdown opens and held fixed
    // while it stays open — so checking/unchecking options doesn't reshuffle rows mid-click.
    // Re-taken the next time the dropdown opens.
    const [frozenOptions, setFrozenOptions] = useState(() =>
        sortSelectedFirst(list, selectedValues)
    );

    const isAtMax =
        typeof maxWords === "number" && selectedValues.length >= maxWords;

    const handleChange = (event, newValue, reason) => {
        if (
            event.type === "keydown" &&
            (event.key === "Backspace" || event.key === "Delete") &&
            reason === "removeOption"
        ) {
            return;
        }

        if (
            maxWords &&
            typeof maxWords === "number" &&
            newValue.length > maxWords
        ) {
            newValue = newValue.slice(0, maxWords);
        }

        const cleanValues = newValue.map((item) =>
            typeof item === "string" ? item : item.label
        );
        onChange(cleanValues);

        // Picking an existing option (checkbox click, or Enter on a highlighted option) keeps
        // the typed search term so the user can keep picking more matches for it. Only freeSolo
        // text committed via Enter (createOption) clears the input, ready for the next term.
        if (reason === "createOption") {
            setInputValue("");
        }
    };

    return (
        <>
            <Autocomplete
                fullWidth
                multiple
                options={inputValue ? list : frozenOptions}
                clearIcon={null}
                freeSolo
                value={selectedValues}
                disableCloseOnSelect
                isOptionEqualToValue={(option, value) => option.label === value}
                inputValue={inputValue}
                onInputChange={(event, newInputValue, reason) => {
                    // MUI resets the input to "" right after a selection commits (both
                    // selectOption and createOption) — we handle clearing ourselves in
                    // handleChange instead, so ignore that automatic reset here.
                    if (reason === "reset") return;

                    setInputValue(newInputValue);
                    onInputValueChange?.(newInputValue);
                }}
                onOpen={() => setFrozenOptions(sortSelectedFirst(list, selectedValues))}
                {...(showDropDown === false && { open: false })}
                sx={{
                    ...theme.typography.customInput,
                    ".MuiInputBase-root": {
                        minHeight,
                        paddingTop: "18px",
                        paddingBottom: maxWords ? "24px" : "8px",
                        position: "relative",
                        alignItems: "start",
                    },
                    "label[data-shrink='true']": {
                        top: "12px",
                    },
                    ".MuiAutocomplete-input": {
                        display: isAtMax ? "none !important" : "block",
                    },
                }}
                onChange={handleChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;

                    return (
                        <li key={key} {...optionProps}>
                            <Checkbox
                                style={{ marginRight: 8 }}
                                checked={selected}
                            />
                            {option.label}
                        </li>
                    );
                }}
                renderTags={(value) => {
                    const numTags = value.length;

                    const hasLimit =
                        limitTags !== null && limitTags !== undefined;
                    const shouldCollapse =
                        !isFocused && hasLimit && numTags > limitTags;

                    const visibleValues = shouldCollapse
                        ? value.slice(0, limitTags)
                        : value;

                    const hiddenValues = shouldCollapse
                        ? value.slice(limitTags)
                        : [];
                    const tooltipTitle = hiddenValues.join(", ");

                    return (
                        <>
                            {visibleValues.map((tag, index) => {
                                const isAiTag = aiValues.includes(tag);

                                return (
                                    <Chip
                                        key={tag + index}
                                        label={tag}
                                        size="small"
                                        icon={isAiTag ? <AutoAwesome /> : undefined}
                                        onClick={() => {
                                            (isFocused ||
                                                typeof limitTags !== "number" ||
                                                selectedValues.length <=
                                                    limitTags) &&
                                                onChange(
                                                    selectedValues.filter(
                                                        (o) => o !== tag
                                                    )
                                                );
                                        }}
                                        onDelete={() =>
                                            onChange(
                                                selectedValues.filter(
                                                    (o) => o !== tag
                                                )
                                            )
                                        }
                                        color="secondary"
                                        variant={isAiTag ? "outlined" : "filled"}
                                        sx={{
                                            lineHeight: 4,
                                            height: 30,
                                            p: 0.3,
                                            marginRight: "5px",
                                            marginBottom: "4px",
                                            ...(isAiTag && {
                                                bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12),
                                                "& .MuiChip-icon": { color: "secondary.main", fontSize: "16px" },
                                            }),
                                            "&:hover": {
                                                "& .MuiSvgIcon-root": {
                                                    transform: "scale(1.15)",
                                                },
                                            },
                                            "& .MuiSvgIcon-root": {
                                                transition: "transform 0.2s",
                                                fontSize: "20px",
                                            },
                                        }}
                                    />
                                );
                            })}

                            {shouldCollapse && (
                                <Tooltip
                                    title={tooltipTitle}
                                    arrow
                                    placement="top"
                                >
                                    <Chip
                                        label={`+${numTags - limitTags} More`}
                                        size="small"
                                        color="secondary"
                                        sx={{
                                            height: 30,
                                            marginRight: "5px",
                                            marginBottom: "4px",
                                            cursor: "pointer",
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        fullWidth
                        {...params}
                        variant="outlined"
                        label={label}
                        placeholder={placeholder}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {params.InputProps.endAdornment}
                                    {maxWords && (
                                        <InputAdornment
                                            position="end"
                                            sx={{
                                                position: "absolute",
                                                right: 14,
                                                bottom: 16,
                                                pointerEvents: "none",
                                            }}
                                        >
                                            {isAtMax ? (
                                                <Typography
                                                    variant="caption"
                                                    color="error.main"
                                                    sx={{
                                                        fontSize: "0.75rem",
                                                        fontWeight: "bold",
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    Maximum limit reached (
                                                    {maxWords} words max)
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    variant="caption"
                                                    color="text.light"
                                                    sx={{ fontSize: "0.75rem" }}
                                                >
                                                    {typeof maxWords ===
                                                    "number"
                                                        ? `Max ${maxWords} Words`
                                                        : maxWords}
                                                </Typography>
                                            )}
                                        </InputAdornment>
                                    )}
                                </>
                            ),
                        }}
                    />
                )}
            />
        </>
    );
};

export default AutocompleteWithTags;
